import { Project, ProjectPhase } from 'api/project';
import {
  getEquivalentStatusForApiStatus,
  TimesheetEntryType,
  TimesheetUISourceType,
} from 'api/timesheets';
import { Category } from 'api/timesheets/categories';
import { EnabledSourcesConfiguration } from 'api/timesheets/configuration/configuration';
import { AdhocTask, CalendarIntegrationTask } from '../drawers';
import {
  PhaseTask,
  PhaseTaskPreview,
  ProjectRow,
  ProjectTask,
  TimeEntryState,
  TimesheetAdhocTask,
  TimesheetCalendarIntegrationEntry,
  TimesheetEntry,
  TimesheetTableTimeEntry,
  TimesheetTask,
  TimesheetWeeklyRow,
  TimesheetWeeklyRowTask,
} from '../types';

export const findTaskInTimesheetRows = (
  timesheetWeeklyRows: TimesheetWeeklyRow[],
  searchTaskId: number,
): TimesheetWeeklyRow | undefined => {
  return timesheetWeeklyRows.find(
    ({ task }) =>
      (task as TimesheetTask).id === searchTaskId &&
      task.entryType === TimesheetEntryType.TaskEntry,
  );
};

export const findProjectInTimesheetRows = (
  timesheetWeeklyRows: TimesheetWeeklyRow[],
  searchProjectId: number,
): TimesheetWeeklyRow | undefined => {
  return timesheetWeeklyRows.find(
    ({ task }) =>
      (task as ProjectRow)?.project?.id === searchProjectId &&
      task.entryType === TimesheetEntryType.ProjectEntry,
  );
};

export const findPhaseInTimesheetRows = (
  timesheetWeeklyRows: TimesheetWeeklyRow[],
  searchPhaseId: number,
  searchProjectId: number,
): TimesheetWeeklyRow | undefined =>
  timesheetWeeklyRows.find(
    ({ task }) =>
      (task as PhaseTask)?.project?.id === searchProjectId &&
      (task as PhaseTask)?.projectPhase?.id === searchPhaseId &&
      task.entryType === TimesheetEntryType.PhaseEntry,
  );

export const findAdhocTaskInTimesheetRows = (
  timesheetWeeklyRows: TimesheetWeeklyRow[],
  activityName: string,
  projectId?: number,
): TimesheetWeeklyRow | undefined => {
  return timesheetWeeklyRows.find(({ task }) => {
    return (
      (task as TimesheetAdhocTask).activityName === activityName &&
      (task as TimesheetAdhocTask).project?.id === projectId
    );
  });
};

export const findRowInTimesheetRows = (
  timesheetWeeklyRows: TimesheetWeeklyRow[],
  timeEntry: TimesheetEntry,
): TimesheetWeeklyRow | undefined => {
  if (timeEntry.type === TimesheetEntryType.TaskEntry) {
    return findTaskInTimesheetRows(timesheetWeeklyRows, timeEntry.task.id);
  }

  if (timeEntry.type === TimesheetEntryType.ProjectEntry) {
    return findProjectInTimesheetRows(
      timesheetWeeklyRows,
      timeEntry.project.id,
    );
  }

  if (timeEntry.type === TimesheetEntryType.PhaseEntry) {
    return findPhaseInTimesheetRows(
      timesheetWeeklyRows,
      timeEntry.projectPhase.id,
      timeEntry.project.id,
    );
  }

  return findAdhocTaskInTimesheetRows(
    timesheetWeeklyRows,
    timeEntry.activityName,
    timeEntry.project?.id,
  );
};

export const convertRowDataToTimesheet = (
  task: TimesheetCalendarIntegrationEntry,
): CalendarIntegrationTask => ({
  activityName: task.activityName,
  billable: task.billable,
  project: {
    label: task.project.name,
    value: task.project.id,
  },
  category: task.category
    ? {
        label: task.category.categoryName,
        value: task.category.categoryId,
      }
    : undefined,
  sourceId: task.sourceId,
  sourceType: task.sourceType,
});

export const isSameTask = (
  taskA: TimesheetWeeklyRowTask,
  taskB: TimesheetWeeklyRowTask,
): boolean => {
  if (taskA.entryType !== taskB.entryType) {
    return false;
  }
  if (taskA.entryType === TimesheetEntryType.TaskEntry) {
    return (taskA as TimesheetTask).id === (taskB as TimesheetTask).id;
  }

  if (taskA.entryType === TimesheetEntryType.ProjectEntry) {
    return (
      (taskA as ProjectRow).project?.id === (taskB as ProjectRow).project?.id
    );
  }

  if (taskA.entryType === TimesheetEntryType.PhaseEntry) {
    return (
      (taskA as PhaseTask).projectPhase.id ===
        (taskB as PhaseTask).projectPhase.id &&
      (taskA as PhaseTask).project?.id === (taskB as PhaseTask).project?.id
    );
  }

  return (
    (taskA as TimesheetAdhocTask).activityName ===
      (taskB as TimesheetAdhocTask).activityName &&
    (taskA as TimesheetAdhocTask).project?.id ===
      (taskB as TimesheetAdhocTask).project?.id &&
    taskA.entryType === taskB.entryType
  );

  return false;
};

export const deleteEntryFromTimesheetRows = (
  timesheetWeeklyRows: TimesheetWeeklyRow[],
  timeEntry: TimesheetEntry,
): TimesheetWeeklyRow[] => {
  const timesheetTask = findRowInTimesheetRows(
    timesheetWeeklyRows,
    timeEntry,
  )?.task;

  if (!timesheetTask) {
    return timesheetWeeklyRows;
  }

  return timesheetWeeklyRows.map((timesheetRow) => {
    if (isSameTask(timesheetRow.task, timesheetTask)) {
      const { days } = timesheetRow;
      const day = days[timeEntry.date];

      return {
        ...timesheetRow,
        days: {
          ...days,
          [timeEntry.date]: day.filter(
            (childEntry) => childEntry.timeEntryId !== timeEntry.timeEntryId,
          ),
        },
      };
    }

    return timesheetRow;
  });
};

export const transformEntriesIntoWeeklyRows = (
  timesheetTimeEntries: TimesheetEntry[],
  projects: Project[] | undefined,
): TimesheetWeeklyRow[] => {
  const timesheetWeeklyRows: TimesheetWeeklyRow[] = [];

  timesheetTimeEntries.forEach((timeEntry) => {
    if (timeEntry.type === TimesheetEntryType.TaskEntry) {
      const taskId = timeEntry.task.id;
      const addedTask = findTaskInTimesheetRows(timesheetWeeklyRows, taskId);

      if (addedTask) {
        const dayEntry = addedTask.days[timeEntry.date];

        if (dayEntry) {
          dayEntry.push({
            timeEntryId: timeEntry.timeEntryId,
            notes: timeEntry.notes,
            category: timeEntry.category,
            billable: timeEntry.billable,
            minutes: timeEntry.minutes,
            inState: TimeEntryState.Loaded,
          });
        } else {
          addedTask.days[timeEntry.date] = [
            {
              timeEntryId: timeEntry.timeEntryId,
              notes: timeEntry.notes,
              category: timeEntry.category,
              billable: timeEntry.billable,
              minutes: timeEntry.minutes,
              inState: TimeEntryState.Loaded,
            },
          ];
        }
      } else {
        timesheetWeeklyRows.push({
          task: {
            ...timeEntry.task,
            project: { ...timeEntry.project },
            entryType: TimesheetEntryType.TaskEntry,
            sourceType: TimesheetUISourceType.Task,
            sourceId: timeEntry.task.id,
            approvalStatus: getEquivalentStatusForApiStatus(timeEntry.status),
          },
          days: {
            [timeEntry.date]: [
              {
                timeEntryId: timeEntry.timeEntryId,
                notes: timeEntry.notes,
                category: timeEntry.category,
                billable: timeEntry.billable,
                minutes: timeEntry.minutes,
                inState: TimeEntryState.Loaded,
              },
            ],
          },
        });
      }
    } else if (timeEntry.type === TimesheetEntryType.ProjectEntry) {
      const projectId = timeEntry.project?.id;
      const addedTask = findProjectInTimesheetRows(
        timesheetWeeklyRows,
        projectId,
      );

      if (addedTask) {
        const dayEntry = addedTask.days[timeEntry.date];

        if (dayEntry) {
          dayEntry.push({
            timeEntryId: timeEntry.timeEntryId,
            notes: timeEntry.notes,
            category: timeEntry.category,
            billable: timeEntry.billable,
            minutes: timeEntry.minutes,
            inState: TimeEntryState.Loaded,
          });
        } else {
          addedTask.days[timeEntry.date] = [
            {
              timeEntryId: timeEntry.timeEntryId,
              notes: timeEntry.notes,
              category: timeEntry.category,
              billable: timeEntry.billable,
              minutes: timeEntry.minutes,
              inState: TimeEntryState.Loaded,
            },
          ];
        }
      } else {
        timesheetWeeklyRows.push({
          task: {
            project: { ...timeEntry.project },
            entryType: TimesheetEntryType.ProjectEntry,
            sourceId: timeEntry.project.id,
            sourceType: TimesheetUISourceType.Project,
            billable: timeEntry.billable,
            approvalStatus: getEquivalentStatusForApiStatus(timeEntry.status),
          },
          days: {
            [timeEntry.date]: [
              {
                timeEntryId: timeEntry.timeEntryId,
                notes: timeEntry.notes,
                category: timeEntry.category,
                billable: timeEntry.billable,
                minutes: timeEntry.minutes,
                inState: TimeEntryState.Loaded,
              },
            ],
          },
        });
      }
    } else if (timeEntry.type === TimesheetEntryType.PhaseEntry) {
      const phaseId = timeEntry.projectPhase.id;
      const projectId = timeEntry.project.id;
      const addedTask = findPhaseInTimesheetRows(
        timesheetWeeklyRows,
        phaseId,
        projectId,
      );
      const project = projects?.find((x: Project) => x.projectId === projectId);
      const phase = project?.projectPhases?.find(
        (x: ProjectPhase) => x.projectPhaseId === phaseId,
      );

      if (addedTask) {
        const dayEntry = addedTask.days[timeEntry.date];

        if (dayEntry) {
          dayEntry.push({
            timeEntryId: timeEntry.timeEntryId,
            notes: timeEntry.notes,
            category: timeEntry.category,
            billable: timeEntry.billable,
            minutes: timeEntry.minutes,
            inState: TimeEntryState.Loaded,
          });
        } else {
          addedTask.days[timeEntry.date] = [
            {
              timeEntryId: timeEntry.timeEntryId,
              notes: timeEntry.notes,
              category: timeEntry.category,
              billable: timeEntry.billable,
              minutes: timeEntry.minutes,
              inState: TimeEntryState.Loaded,
            },
          ];
        }
      } else {
        timesheetWeeklyRows.push({
          task: {
            project: { ...timeEntry.project },
            projectPhase: { ...timeEntry.projectPhase },
            entryType: TimesheetEntryType.PhaseEntry,
            sourceId: timeEntry.projectPhase.id,
            sourceType: TimesheetUISourceType.Phase,
            billable: timeEntry.billable,
            startDate: phase?.startDate,
            dueDate: phase?.dueDate,
            private: phase?.private,
            status: phase?.fields[0].fieldValue as number,
            approvalStatus: getEquivalentStatusForApiStatus(timeEntry.status),
          },
          days: {
            [timeEntry.date]: [
              {
                timeEntryId: timeEntry.timeEntryId,
                notes: timeEntry.notes,
                category: timeEntry.category,
                billable: timeEntry.billable,
                minutes: timeEntry.minutes,
                inState: TimeEntryState.Loaded,
              },
            ],
          },
        });
      }
    } else if (
      [TimesheetEntryType.AdhocEntry, TimesheetEntryType.Integration].includes(
        timeEntry.type,
      )
    ) {
      const { activityName, project } = timeEntry;
      const addedTask = findAdhocTaskInTimesheetRows(
        timesheetWeeklyRows,
        activityName,
        project?.id,
      );

      if (addedTask) {
        const dayEntry = addedTask.days[timeEntry.date];

        if (dayEntry) {
          dayEntry.push({
            timeEntryId: timeEntry.timeEntryId,
            notes: timeEntry.notes,
            category: timeEntry.category,
            billable: timeEntry.billable,
            minutes: timeEntry.minutes,
            inState: TimeEntryState.Loaded,
          });
        } else {
          addedTask.days[timeEntry.date] = [
            {
              timeEntryId: timeEntry.timeEntryId,
              notes: timeEntry.notes,
              category: timeEntry.category,
              billable: timeEntry.billable,
              minutes: timeEntry.minutes,
              inState: TimeEntryState.Loaded,
            },
          ];
        }

        const task = addedTask.task as TimesheetAdhocTask;

        if (timeEntry.category && !task.defaultCategoryId) {
          task.defaultCategoryId = timeEntry.category.categoryId;
        }
      } else {
        const task =
          timeEntry.type === TimesheetEntryType.AdhocEntry
            ? {
                activityName: timeEntry.activityName,
                sourceType: TimesheetUISourceType.Adhoc,
                project,
                entryType: timeEntry.type,
                defaultBillable: timeEntry.billable,
                defaultCategoryId: timeEntry.category?.categoryId,
                approvalStatus: getEquivalentStatusForApiStatus(
                  timeEntry.status,
                ),
              }
            : {
                activityName: timeEntry.activityName,
                project,
                entryType: timeEntry.type,
                defaultBillable: timeEntry.billable,
                defaultCategoryId: timeEntry.category?.categoryId,
                sourceType: timeEntry.sourceType,
                sourceId: timeEntry.sourceId,
                approvalStatus: getEquivalentStatusForApiStatus(
                  timeEntry.status,
                ),
              };

        timesheetWeeklyRows.push({
          task,
          days: {
            [timeEntry.date]: [
              {
                timeEntryId: timeEntry.timeEntryId,
                notes: timeEntry.notes,
                category: timeEntry.category,
                billable: timeEntry.billable,
                minutes: timeEntry.minutes,
                inState: TimeEntryState.Loaded,
              },
            ],
          },
        });
      }
    }
  });

  return timesheetWeeklyRows;
};

export const findTimeEntryinState = (
  entries: TimesheetEntry[],
  timeEntryId: number,
): TimesheetEntry | undefined =>
  entries.find((entry) => entry.timeEntryId === timeEntryId);

export const removeDuplicateEntry = (
  tasks: CalendarIntegrationTask[],
): CalendarIntegrationTask[] => {
  const stringify = (task) =>
    JSON.stringify({ name: task.activityName, project: task.project.value });
  const uniqueTask = {};

  tasks.forEach((task) => {
    uniqueTask[stringify(task)] = task;
  });

  return Object.values(uniqueTask);
};

export const isSameActivity = (
  activity: AdhocTask,
  uniqueActivity: AdhocTask,
): boolean => {
  return (
    activity.activityName === uniqueActivity.activityName &&
    activity.project?.label === uniqueActivity.project?.label &&
    activity.project?.value === uniqueActivity.project?.value
  );
};

export const isSameProject = (
  project: ProjectTask,
  uniqueProject: ProjectTask,
): boolean => {
  return (
    project.project?.label === uniqueProject.project?.label &&
    project.project?.value === uniqueProject.project?.value
  );
};

export const isSamePhaseTask = (
  phase: PhaseTaskPreview,
  uniquePhase: PhaseTaskPreview,
): boolean => {
  return (
    phase.project?.id === uniquePhase.project?.id &&
    phase.projectPhase?.id === uniquePhase.projectPhase?.id
  );
};

export const getUniqueAdhocActivitiesFromEntries = (
  entries: TimesheetEntry[],
): AdhocTask[] => {
  const activityArray = entries.reduce((acc: AdhocTask[], entry) => {
    if (entry.type === TimesheetEntryType.AdhocEntry) {
      acc.push({
        project: {
          label: entry.project?.name,
          value: entry.project?.id,
        },
        activityName: entry.activityName,
        billable: entry.billable,
      });
    }

    return acc;
  }, [] as AdhocTask[]);

  const uniqueActivityArray: AdhocTask[] = [];

  activityArray.forEach((activity) => {
    const activityFound = uniqueActivityArray.find((uniqueActivity) =>
      isSameActivity(activity, uniqueActivity),
    );

    if (!activityFound) {
      uniqueActivityArray.push(activity);
    }
  });

  return uniqueActivityArray;
};

export const getUniqueProjectsFromEntries = (
  entries: TimesheetEntry[],
): ProjectTask[] => {
  const projectArray = entries.reduce((acc: ProjectTask[], entry) => {
    if (
      entry.type === TimesheetEntryType.ProjectEntry &&
      !entry.project.deleted &&
      !entry.project.archived
    ) {
      acc.push({
        project: {
          label: entry.project.name,
          value: entry.project.id,
        },
        billable: entry.billable,
        category: entry.category,
      });
    }

    return acc;
  }, [] as ProjectTask[]);

  const uniqueProjectArray: ProjectTask[] = [];

  projectArray.forEach((project) => {
    const projectFound = uniqueProjectArray.find((uniqueProject) =>
      isSameProject(project, uniqueProject),
    );

    if (!projectFound) {
      uniqueProjectArray.push(project);
    }
  });

  return uniqueProjectArray;
};

export const getUniquePhasesFromEntries = (
  entries: TimesheetEntry[],
): PhaseTaskPreview[] => {
  const phaseArray = entries.reduce((acc: PhaseTaskPreview[], entry) => {
    if (
      entry.type === TimesheetEntryType.PhaseEntry &&
      !entry.project.deleted &&
      !entry.project.archived &&
      !entry.projectPhase.deleted
    ) {
      acc.push({
        project: {
          ...entry.project,
        },
        projectPhase: { ...entry.projectPhase },
        billable: entry.billable,
        category: entry?.category,
      });
    }

    return acc;
  }, [] as PhaseTaskPreview[]);

  const uniquePhaseArray: PhaseTaskPreview[] = [];

  phaseArray.forEach((phase) => {
    const projectFound = uniquePhaseArray.find((uniquePhase) =>
      isSamePhaseTask(phase, uniquePhase),
    );

    if (!projectFound) {
      uniquePhaseArray.push(phase);
    }
  });

  return uniquePhaseArray;
};

export const checkTimeEntryConfig = (
  timesheetEntriesConfig: EnabledSourcesConfiguration,
): { isOnlyProjectsEnabled: boolean; isPhaseEnabled: boolean } => {
  const {
    PROJECT: projectsConfig,
    PHASE: phaseConfig,
    ...rest
  } = timesheetEntriesConfig;
  const isOnlyProjectsEnabled =
    Object.values(rest).every((x) => x === false) &&
    phaseConfig === false &&
    projectsConfig === true;
  const isPhaseEnabled =
    Object.values(rest).every((x) => x === false) && phaseConfig === true;

  return { isOnlyProjectsEnabled, isPhaseEnabled };
};

export const getRowKey = (task: TimesheetWeeklyRowTask, index) => {
  switch (task.entryType) {
    case TimesheetEntryType.TaskEntry:
      return `${task.id}_${task.project.id}`;
    case TimesheetEntryType.ProjectEntry:
      return `${index}_${task.project?.id ?? ''}`;
    case TimesheetEntryType.PhaseEntry:
      return `${task.projectPhase.id}_${task.project?.id ?? ''}`;
    default:
      return `${task.activityName}_${task.project?.id ?? ''}`;
  }
};

export const showTaskColumn = (
  isOnlyProjectsEnabled: boolean,
  data: TimesheetWeeklyRow[],
): boolean => {
  if (isOnlyProjectsEnabled) {
    if (
      data.find((row) => row.task.entryType !== TimesheetEntryType.ProjectEntry)
    ) {
      return true;
    }

    return false;
  }

  return true;
};

export const getMinutes = (
  days: Record<string, TimesheetTableTimeEntry[]>,
): number => {
  return Object.keys(days).reduce((acc, curr) => {
    days[curr].forEach((x) => {
      // eslint-disable-next-line no-param-reassign
      acc += x.minutes;
    });

    return acc;
  }, 0);
};

export const updateCategory = (
  category: Category,
  weeklyRows: TimesheetWeeklyRow[],
  updateTask: TimesheetWeeklyRowTask,
): TimesheetWeeklyRow[] => {
  const row = weeklyRows.map((weeklyRow) => {
    if (isSameTask(weeklyRow.task, updateTask)) {
      const entries = Object.entries(weeklyRow.days);

      return {
        task: {
          ...weeklyRow.task,
          defaultCategoryId: category.id,
        },
        days: entries.reduce((days, [day, dayEntries]) => {
          return {
            ...days,
            [day]: dayEntries.map((e) => {
              if (e.minutes !== 0) {
                return e;
              }

              return {
                ...e,
                category: {
                  categoryId: category.id,
                  categoryName: category.name,
                },
              };
            }),
          };
        }, {}),
      };
    }

    return weeklyRow;
  });

  return row;
};
